
import { useEffect, useRef, useState } from 'react';
import { ShieldCheckIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

const RECAPTCHA_SCRIPT_ID = 'scrambl-recaptcha-script';
let recaptchaScriptPromise: Promise<void> | null = null;

function loadReCaptchaScript(siteKey: string): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA is not available'));
  }

  // Check if grecaptcha enterprise is already fully loaded and ready
  if (window.grecaptcha?.enterprise?.execute) {
    return Promise.resolve();
  }

  if (recaptchaScriptPromise) {
    return recaptchaScriptPromise;
  }

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      // Script exists - wait for grecaptcha to be ready
      const checkReady = () => {
        if (window.grecaptcha?.enterprise?.execute) {
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    // Load with render=siteKey for automatic badge and script initialization
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Wait for grecaptcha.enterprise to be available
      const checkReady = () => {
        if (window.grecaptcha?.enterprise?.execute) {
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    };
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
}

export interface ReCaptchaProps {
  siteKey: string;
  onChange: (token: string | null) => void;
  action?: string;
  className?: string;
}

export function ReCaptcha({ siteKey, onChange, action = 'submit', className }: ReCaptchaProps) {
  const onChangeRef = useRef(onChange);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasExecutedRef = useRef(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let isMounted = true;
    setLoadError(null);

    void loadReCaptchaScript(siteKey)
      .then(() => {
        if (!isMounted) return;
        setIsReady(true);

        // Execute immediately to get token
        if (!hasExecutedRef.current && window.grecaptcha?.enterprise) {
          hasExecutedRef.current = true;
          window.grecaptcha.enterprise.ready(async () => {
            if (!isMounted || !window.grecaptcha?.enterprise) return;
            try {
              const token = await window.grecaptcha.enterprise.execute(siteKey, { action });
              if (isMounted) {
                onChangeRef.current(token);
              }
            } catch {
              if (isMounted) {
                setLoadError('CAPTCHA verification failed. Please refresh.');
                onChangeRef.current(null);
              }
            }
          });
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setLoadError('Unable to load CAPTCHA. Please refresh.');
        onChangeRef.current(null);
      });

    return () => {
      isMounted = false;
    };
  }, [siteKey, action]);

  if (loadError) {
    return <p className="text-xs text-destructive">{loadError}</p>;
  }

  // Score-based reCAPTCHA is invisible - show a subtle indicator.
  // The Google ToS / Privacy Policy links are required when the floating
  // badge is hidden via CSS (see .grecaptcha-badge rule in index.css).
  return (
    <div className={cn('flex items-center gap-2 text-xs text-muted-foreground', className)}>
      <ShieldCheckIcon className={cn('w-4 h-4 shrink-0', isReady ? 'text-green-600' : 'text-muted-foreground')} />
      <span>
        {isReady ? 'Protected by reCAPTCHA' : 'Loading verification...'}
        {isReady && (
          <>
            {' — '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Privacy
            </a>
            {' & '}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Terms
            </a>
          </>
        )}
      </span>
    </div>
  );
}
